import { mediaQueryLarge, mediaQueryHeaderMenu } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {MediaQueryList} */
  #desktopMediaQuery = mediaQueryLarge;

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /**
   * Compact / "mobile" side of disable + open-by-default attrs.
   * Default: Horizon `mediaQueryLarge` (750px). Override with
   * `data-desktop-breakpoint="header-menu"` (HEADER_MENU_DESKTOP_MIN / 1020)
   * or `data-desktop-min` (custom px).
   * @returns {boolean}
   */
  get #isCompactBreakpoint() {
    return !this.#desktopMediaQuery.matches;
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#desktopMediaQuery = this.#resolveDesktopMediaQuery();
    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.#desktopMediaQuery.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * @returns {MediaQueryList}
   */
  #resolveDesktopMediaQuery() {
    if (this.dataset.desktopBreakpoint === 'header-menu') {
      return mediaQueryHeaderMenu;
    }

    const desktopMin = this.dataset.desktopMin;

    if (desktopMin) {
      return matchMedia(`(min-width: ${desktopMin}px)`);
    }

    return mediaQueryLarge;
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isCompact = this.#isCompactBreakpoint;
    const isDesktop = !isCompact;

    // Stop default behaviour from the browser
    if ((isCompact && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isCompact = this.#isCompactBreakpoint;

    this.details.open =
      (isCompact && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isCompact && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
